import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  MapPin, Calendar, Users, Star, MessageSquare, 
  Share2, Heart, PlusCircle, Image as ImageIcon, Map as MapIcon,
  Navigation, Info, CheckCircle2
} from 'lucide-react';
import { getVenueDetails } from '../services/api';
import { useSportsLang } from '../contexts/SportsLangContext';

const LABELS = {
  pt: {
    aquatic: 'Aquático',
    reviews: 'avaliações',
    checkin: 'Fazer Check-in',
    save: 'Salvar',
    shareExp: (n) => `Compartilhe sua experiência no ${n}...`,
    addPhoto: 'Adicionar Foto',
    rate: 'Avaliar',
    directions: 'Rotas',
    map: 'Mapa',
    schedule: 'Agenda',
    noEvents: 'Nenhum evento agendado.',
    amenities: 'Comodidades',
    noAmenities: 'Nenhuma informação disponível.',
    home: 'Home',
    venues: 'Locais',
    tabFeed: 'Feed Social',
    tabAbout: 'Sobre',
    tabEvents: 'Eventos',
    tabGallery: 'Fotos',
    aboutTitle: 'Sobre o Local',
    noDesc: 'Nenhuma descrição disponível.',
    capacity: 'Capacidade',
    opened: 'Abertura',
    teams: 'Times',
    upcomingEvents: 'Próximos Eventos',
    galleryTitle: 'Galeria de Fotos',
    gallerySoon: 'Em breve você poderá ver fotos da comunidade e compartilhar suas próprias experiências.',
    notFound: 'Local não encontrado.',
    backToVenues: '← Voltar para Locais',
    dateLocale: 'pt-BR',
  },
  en: {
    aquatic: 'Aquatic',
    reviews: 'reviews',
    checkin: 'Check-in',
    save: 'Save',
    shareExp: (n) => `Share your experience at ${n}...`,
    addPhoto: 'Add Photo',
    rate: 'Rate',
    directions: 'Directions',
    map: 'Map',
    schedule: 'Schedule',
    noEvents: 'No events scheduled.',
    amenities: 'Amenities',
    noAmenities: 'No information available.',
    home: 'Home',
    venues: 'Venues',
    tabFeed: 'Social Feed',
    tabAbout: 'About',
    tabEvents: 'Events',
    tabGallery: 'Photos',
    aboutTitle: 'About This Venue',
    noDesc: 'No description available.',
    capacity: 'Capacity',
    opened: 'Opened',
    teams: 'Teams',
    upcomingEvents: 'Upcoming Events',
    galleryTitle: 'Photo Gallery',
    gallerySoon: 'Soon you will be able to see community photos and share your own experiences.',
    notFound: 'Venue not found.',
    backToVenues: '← Back to Venues',
    dateLocale: 'en-US',
  }
};

// === CORES & TEMA ===
const THEME = {
  primary: '#111111',
  gold: '#b48c2c',
  bg: '#f3f2ef',
  white: '#ffffff',
  text: '#1f2937'
};

// === HELPER: TRATAMENTO DE IMAGEM (Mantendo consistência com VenuesPage) ===
const getPhotoFileName = (photo) => {
  if (!photo) return null;
  try {
    let fileName = photo.split('/').pop().split('\\').pop();
    const nameMap = {
      'arenamrv.jpg': 'arena-mrv1.jpg',
      'arenamrv1.jpg': 'arena-mrv1.jpg',
      'arenamrv2.jpg': 'arena-mrv2.jpg',
      'arenamrv3.jpg': 'arena-mrv3.jpg',
      'arenamrv-1.jpg': 'arena-mrv1.jpg',
      'arena-mrv-1.jpg': 'arena-mrv1.jpg',
      'arena-mrv-2.jpg': 'arena-mrv2.jpg',
      'arena-mrv-3.jpg': 'arena-mrv3.jpg',
      'independencia.jpg': 'arena-independencia1.jpg',
      'independencia1.jpg': 'arena-independencia1.jpg',
      'independencia2.jpg': 'arena-independencia2.jpg',
      'independencia3.jpg': 'arena-independencia3.jpg',
      'independencia-1.jpg': 'arena-independencia1.jpg',
      'arena-independencia-1.jpg': 'arena-independencia1.jpg',
      'arena-independencia-2.jpg': 'arena-independencia2.jpg',
      'arena-independencia-3.jpg': 'arena-independencia3.jpg',
      'unibh.jpg': 'arena-unibh-minas1.jpg',
      'unibh1.jpg': 'arena-unibh-minas1.jpg',
      'unibh2.jpg': 'arena-unibh-minas2.jpg',
      'unibh3.jpg': 'arena-unibh-minas3.jpg',
      'arena-unibh-1.jpg': 'arena-unibh-minas1.jpg',
      'arena-unibh-2.jpg': 'arena-unibh-minas2.jpg',
      'arena-unibh-3.jpg': 'arena-unibh-minas3.jpg',
      'mineirao.jpg': 'mineirao1.jpg',
      'mineirao1.jpg': 'mineirao1.jpg',
      'mineirao2.jpg': 'mineirao2.jpg',
      'mineirao3.jpg': 'mineirao3.jpg',
      'mineirao-1.jpg': 'mineirao1.jpg',
      'mineirao-2.jpg': 'mineirao2.jpg',
      'mineirao-3.jpg': 'mineirao3.jpg',
      'parqueaquaticomineiras1.jpg': 'minasparqueaquatico1.jpg',
      'parqueaquaticopaineiras1.jpg': 'parqueaquaticopaineiras1.jpg',
      'parqueaquaticopaineiras1': 'parqueaquaticopaineiras1.jpg',
      'polisportivoriacho1.jpg': 'polisportivoriacho1.jpeg',
      'polisportivoriacho1': 'polisportivoriacho1.jpeg',
      'polisportivoriacho1.jpeg': 'polisportivoriacho1.jpeg'
    };
    
    fileName = fileName.replace(/-(\d)/, '$1');
    if (nameMap[fileName]) fileName = nameMap[fileName];
    else fileName = fileName.toLowerCase();
    
    if (!/\.(jpg|jpeg|webp)$/i.test(fileName)) fileName = `${fileName}.jpg`;
    return fileName;
  } catch {
    return photo;
  }
};

// === COMPONENTE 1: HEADER DE DESTINO (IMERSIVO) ===
const VenueHeader = ({ venue }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const photoName = getPhotoFileName(venue.mainPhoto);
  const imageSrc = photoName ? `/arenas/${photoName}` : null;
  
  return (
    <div className="relative h-72 md:h-96 w-full group">
      <div className="absolute inset-0 bg-gray-900">
        {imageSrc ? (
          <img 
            src={imageSrc} 
            alt={venue.name} 
            className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-1000"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#111111] to-[#0b0b0c]"
          style={{ display: imageSrc ? 'none' : 'flex' }}
        >
          <span className="text-white opacity-20 text-6xl font-bold">{venue.name?.substring(0, 2)}</span>
        </div>
      </div>
      
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>

      <div className="absolute bottom-0 left-0 w-full p-6 md:p-10 z-20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-end gap-6">
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-white uppercase tracking-wider border border-white/20">
                {venue.type === 'parque-aquatico' ? t.aquatic : venue.type}
              </span>
              <div className="flex items-center text-yellow-400 text-sm font-bold gap-1">
                <Star size={14} fill="currentColor" /> 4.8 (1.2k {t.reviews})
              </div>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-2 leading-tight">
              {venue.name}
            </h1>
            
            <div className="flex flex-wrap gap-4 text-gray-300 text-sm">
              <span className="flex items-center gap-1">
                <MapPin size={16} /> {venue.city}, {venue.country}
              </span>
              <span className="flex items-center gap-1">
                <Users size={16} /> Cap: {venue.capacity?.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="flex gap-3 w-full md:w-auto">
             <button className="flex-1 md:flex-none px-6 py-3 bg-[#111111] text-white rounded-xl font-bold hover:bg-[#0b0b0c] transition-colors shadow-lg flex items-center justify-center gap-2">
               <CheckCircle2 size={18} />
               {t.checkin}
             </button>
             <button className="px-4 py-3 bg-white/10 backdrop-blur-md border border-white/30 text-white rounded-xl font-bold hover:bg-white/20 transition-colors flex items-center justify-center gap-2">
               <Heart size={18} />
               {t.save}
             </button>
             <button className="px-4 py-3 bg-white/10 backdrop-blur-md border border-white/30 text-white rounded-xl font-bold hover:bg-white/20 transition-colors">
               <Share2 size={18} />
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// === COMPONENTE 2: FEED DE ATIVIDADE DO LOCAL ===
const VenueFeed = ({ venue }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <div className="space-y-6">
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex gap-3">
      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-[#111111] font-bold">EU</div>
      <div className="flex-1">
        <input 
          type="text" 
          placeholder={t.shareExp(venue.name)} 
          className="w-full bg-gray-50 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111111]/20 transition-all hover:bg-gray-100"
        />
        <div className="flex gap-4 mt-3 ml-2">
          <button className="flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-[#111111]">
            <ImageIcon size={16} /> {t.addPhoto}
          </button>
          <button className="flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-[#111111]">
            <Star size={16} /> {t.rate}
          </button>
        </div>
      </div>
    </div>

    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
       <div className="flex justify-between items-start mb-3">
         <div className="flex items-center gap-3">
           <div className="w-10 h-10 rounded-full bg-gray-200"></div>
           <div>
             <h4 className="font-bold text-sm text-gray-900">Ana Souza</h4>
             <p className="text-xs text-gray-500">Esteve aqui ontem • <span className="text-[#111111]">Jogo do Palmeiras</span></p>
           </div>
         </div>
         <div className="flex text-yellow-400">
           {[1,2,3,4,5].map(i => <Star key={i} size={12} fill="currentColor" />)}
         </div>
       </div>
       <p className="text-gray-800 text-sm mb-3">
         A atmosfera estava incrível! A acústica desse estádio é sensacional. Só a fila da lanchonete que estava um pouco grande no intervalo.
       </p>
       <div className="rounded-lg overflow-hidden h-48 bg-gray-100 mb-3">
         <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
           <ImageIcon size={32} />
         </div>
       </div>
       <div className="flex gap-4 pt-2 border-t border-gray-50">
         <button className="text-xs font-bold text-gray-500 flex items-center gap-1"><Heart size={14} /> 24</button>
         <button className="text-xs font-bold text-gray-500 flex items-center gap-1"><MessageSquare size={14} /> 5</button>
       </div>
    </div>
  </div>
  );
};

// === COMPONENTE 3: INFO SIDEBAR ===
const VenueInfoSidebar = ({ venue }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <div className="space-y-6">
    
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-1">
      <div className="bg-gray-100 h-32 rounded-lg relative mb-3">
        <div className="absolute inset-0 flex items-center justify-center opacity-30 bg-[url('https://upload.wikimedia.org/wikipedia/commons/e/ec/World_map_blank_without_borders.svg')] bg-cover"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <MapPin className="text-red-500 drop-shadow-md" size={32} fill="currentColor" />
        </div>
      </div>
      <div className="px-3 pb-3">
        <p className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2">{venue.address || `${venue.city}, ${venue.country}`}</p>
        <div className="flex gap-2">
          <button className="flex-1 py-2 rounded-lg bg-gray-50 text-xs font-bold text-gray-700 hover:bg-gray-100 flex items-center justify-center gap-1">
            <Navigation size={14} /> {t.directions}
          </button>
          <button className="flex-1 py-2 rounded-lg bg-gray-50 text-xs font-bold text-gray-700 hover:bg-gray-100 flex items-center justify-center gap-1">
            <MapIcon size={14} /> {t.map}
          </button>
        </div>
      </div>
    </div>

    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <h3 className="font-bold text-sm text-gray-900 mb-4 flex items-center gap-2">
        <Calendar size={16} className="text-[#111111]" />
        {t.schedule}
      </h3>
      <div className="space-y-3">
        {venue.nextEvents && venue.nextEvents.length > 0 ? (
          venue.nextEvents.map((event, i) => (
            <div key={i} className="flex gap-3 pb-3 border-b border-gray-50 last:border-0 last:pb-0">
               <div className="bg-gray-100 rounded-lg p-2 text-center min-w-[50px]">
                 <span className="block text-xs font-bold text-gray-500 uppercase">
                   {new Date(event.date).toLocaleDateString(t.dateLocale, {month: 'short'})}
                 </span>
                 <span className="block text-lg font-bold text-gray-900 leading-none">
                   {new Date(event.date).getDate()}
                 </span>
               </div>
               <div>
                 <p className="text-sm font-bold text-gray-900 line-clamp-1">
                   {event.homeTeam} vs {event.awayTeam}
                 </p>
                 <p className="text-xs text-gray-500">{event.competition}</p>
               </div>
            </div>
          ))
        ) : (
          <p className="text-xs text-gray-400 italic">{t.noEvents}</p>
        )}
      </div>
    </div>

    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <h3 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2">
        <Info size={16} className="text-[#111111]" />
        {t.amenities}
      </h3>
      <div className="flex flex-wrap gap-2">
        {venue.facilities && venue.facilities.length > 0 ? (
          venue.facilities.map((fac, i) => (
            <span key={i} className="text-xs bg-gray-50 border border-gray-200 px-2 py-1 rounded text-gray-600">
              {fac}
            </span>
          ))
        ) : (
          <p className="text-xs text-gray-400 italic">{t.noAmenities}</p>
        )}
      </div>
    </div>

  </div>
  );
};

// === PÁGINA PRINCIPAL ===
const VenueDetailPage = () => {
  const { venueId } = useParams();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('feed');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getVenueDetails(venueId);
        if (response.data?.success) {
          setVenue(response.data.venue);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (venueId) fetchData();
  }, [venueId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: THEME.bg }}>
        <div className="animate-spin w-12 h-12 border-4 border-t-[#111111] border-gray-200 rounded-full"></div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: THEME.bg }}>
        <div className="text-center">
          <p className="text-gray-600 mb-4">{t.notFound}</p>
          <Link to="/locais" className="text-[#111111] font-bold hover:underline">
            {t.backToVenues}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12 font-sans" style={{ backgroundColor: THEME.bg }}>
      
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-900">{t.home}</Link> / 
          <Link to="/locais" className="hover:text-gray-900">{t.venues}</Link> / 
          <span className="font-bold text-gray-900">{venue.name}</span>
        </div>
      </div>

      <div className="w-full bg-gray-900 mb-6">
         <VenueHeader venue={venue} />
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          <div className="lg:col-span-8 space-y-6">
             
             <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-1 flex overflow-x-auto scrollbar-hide">
               {[
                 { id: 'feed', label: t.tabFeed },
                 { id: 'about', label: t.tabAbout },
                 { id: 'events', label: t.tabEvents },
                 { id: 'gallery', label: t.tabGallery }
               ].map(tab => (
                 <button
                   key={tab.id}
                   onClick={() => setActiveTab(tab.id)}
                   className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${
                     activeTab === tab.id 
                       ? 'bg-gray-100 text-[#111111]' 
                       : 'text-gray-500 hover:bg-gray-50'
                   }`}
                 >
                   {tab.label}
                 </button>
               ))}
             </div>

             {activeTab === 'feed' && <VenueFeed venue={venue} />}
             
             {activeTab === 'about' && (
               <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                 <h3 className="font-bold text-lg mb-4 text-gray-900">{t.aboutTitle}</h3>
                 <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                   {venue.description || t.noDesc}
                 </p>
                 
                 <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
                    <div className="text-center">
                       <div className="text-2xl font-black text-[#111111]">{venue.capacity?.toLocaleString()}</div>
                       <div className="text-xs text-gray-500 uppercase">{t.capacity}</div>
                    </div>
                    <div className="text-center border-l border-gray-100">
                       <div className="text-2xl font-black text-[#111111]">{venue.yearOpened}</div>
                       <div className="text-xs text-gray-500 uppercase">{t.opened}</div>
                    </div>
                    <div className="text-center border-l border-gray-100">
                       <div className="text-2xl font-black text-[#111111]">{venue.tenantTeamsCount || 0}</div>
                       <div className="text-xs text-gray-500 uppercase">{t.teams}</div>
                    </div>
                 </div>
               </div>
             )}

             {activeTab === 'events' && (
               <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                 <h3 className="font-bold text-lg mb-4 text-gray-900">{t.upcomingEvents}</h3>
                 {venue.nextEvents && venue.nextEvents.length > 0 ? (
                   <div className="space-y-4">
                     {venue.nextEvents.map((event, i) => (
                       <div key={i} className="p-4 border border-gray-100 rounded-lg hover:bg-gray-50">
                         <div className="flex justify-between items-start mb-2">
                           <h4 className="font-bold text-gray-900">{event.homeTeam} vs {event.awayTeam}</h4>
                           <span className="text-xs bg-[#111111] text-white px-2 py-1 rounded">{event.competition}</span>
                         </div>
                         <p className="text-sm text-gray-500">
                           {new Date(event.date).toLocaleDateString(t.dateLocale, {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})} às {new Date(event.date).toLocaleTimeString(t.dateLocale, {hour: '2-digit', minute: '2-digit'})}
                         </p>
                       </div>
                     ))}
                   </div>
                 ) : (
                   <p className="text-gray-400 text-center py-8">{t.noEvents}</p>
                 )}
               </div>
             )}

             {activeTab === 'gallery' && (
               <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm text-center">
                 <ImageIcon size={48} className="mx-auto mb-4 text-gray-300" />
                 <h3 className="font-bold text-lg text-gray-900 mb-2">{t.galleryTitle}</h3>
                 <p className="text-gray-500 text-sm">
                   {t.gallerySoon}
                 </p>
               </div>
             )}

          </div>

          <div className="lg:col-span-4 space-y-6">
             <VenueInfoSidebar venue={venue} />
          </div>

        </div>
      </div>
    </div>
  );
};

export default VenueDetailPage;
